// ? Using a separate toast component for sheet to have it be rendered on top of
// ? the sheet content. Default VnToaster is rendered behind the sheet with no obvious fix.
import { useEffect, useRef } from "react";
import { Animated, Easing } from "react-native";
import { CheckCircle2Icon, XCircleIcon } from "lucide-react-native";

import { VnAlert, VnAlertTitle } from "@/components/VnAlert";
import { VnBox } from "@/components/VnBox";
import {
  TOAST_ANIMATION_DURATION_IN_MS,
  TOAST_DURATION_IN_MS,
} from "@/domain/constants";
import { SheetToastType, ToastVariantType } from "@/hooks/useSheetToast";

type VnSheetToastItemPropsType = {
  toast: SheetToastType;
  onDismiss: (id: string) => void;
  duration?: number;
};

const variantConfig: Record<
  ToastVariantType,
  {
    icon: typeof CheckCircle2Icon;
    variant: "default" | "destructive";
  }
> = {
  success: {
    icon: CheckCircle2Icon,
    variant: "default",
  },
  error: {
    icon: XCircleIcon,
    variant: "destructive",
  },
};

const VnSheetToastItem = ({
  toast,
  onDismiss,
  duration = TOAST_DURATION_IN_MS,
}: VnSheetToastItemPropsType) => {
  const opacity = useRef(new Animated.Value(0)).current;
  const translateY = useRef(new Animated.Value(-20)).current;

  useEffect(() => {
    // Animate in
    Animated.parallel([
      Animated.timing(opacity, {
        toValue: 1,
        duration: TOAST_ANIMATION_DURATION_IN_MS,
        easing: Easing.out(Easing.ease),
        useNativeDriver: true,
      }),
      Animated.timing(translateY, {
        toValue: 0,
        duration: TOAST_ANIMATION_DURATION_IN_MS,
        easing: Easing.out(Easing.back(1.5)),
        useNativeDriver: true,
      }),
    ]).start();

    // Auto dismiss
    const dismissTimer = setTimeout(() => {
      Animated.parallel([
        Animated.timing(opacity, {
          toValue: 0,
          duration: TOAST_ANIMATION_DURATION_IN_MS,
          easing: Easing.in(Easing.ease),
          useNativeDriver: true,
        }),
        Animated.timing(translateY, {
          toValue: -20,
          duration: TOAST_ANIMATION_DURATION_IN_MS,
          easing: Easing.in(Easing.ease),
          useNativeDriver: true,
        }),
      ]).start(() => {
        onDismiss(toast.id);
      });
    }, duration);

    return () => clearTimeout(dismissTimer);
  }, [opacity, translateY, duration, onDismiss, toast.id]);

  const config = variantConfig[toast.variant];

  return (
    <Animated.View
      style={{
        opacity,
        transform: [{ translateY }],
      }}>
      <VnAlert icon={config.icon} variant={config.variant}>
        <VnAlertTitle>{toast.message}</VnAlertTitle>
      </VnAlert>
    </Animated.View>
  );
};

type VnSheetToastContainerPropsType = {
  toasts: SheetToastType[];
  onDismiss: (id: string) => void;
  duration?: number;
};

export const VnSheetToastContainer = ({
  toasts,
  onDismiss,
  duration,
}: VnSheetToastContainerPropsType) => {
  if (toasts.length === 0) return null;

  return (
    <VnBox className="absolute left-5 right-5 top-2 z-50 gap-2">
      {toasts.map(toast => (
        <VnSheetToastItem
          key={toast.id}
          toast={toast}
          onDismiss={onDismiss}
          duration={duration}
        />
      ))}
    </VnBox>
  );
};
